import { datadogRum } from "@datadog/browser-rum";
import { getAppVariable } from "@white-label-helper/get-app-variable";
import { METRIC_TYPE } from "./custom-metric-constants";

/**
 * Record a custom business metric for tracking and monitoring.
 *
 * @param metricType - Metric category: 'tracking' for user actions, 'error' for exceptions
 * @param name - Action or event name (use ACTION_METRIC or ERROR_METRIC constants)
 * @param context - Event payload with custom properties. Values must be primitives (string, number, boolean, null, or undefined)
 * @param startTime - Optional performance timing start (ms from performance.now()). Duration calculated as (now - startTime) / 1000
 * @param contextKey - Feature area or component context (use CONTEXT_KEYS constants for consistency)
 * @param airportCode - Optional airport code for multi-airport analytics
 *
 * @example
 * // Track a user action
 * addCustomMetric(METRIC_TYPE.TRACKING, ACTION_METRIC.PROCEED_TO_PAYMENT, {
 *   fromUrlPath: window.location.href,
 *   toUrlPath: checkoutUrl,
 * }, null, CONTEXT_KEYS.TRAVEL_EXTRAS);
 *
 * // Track performance metric
 * const start = performance.now();
 * await expensiveOperation();
 * addCustomMetric(METRIC_TYPE.TRACKING, 'expensiveOperation', {}, start, CONTEXT_KEYS.FEATURE_AREA);
 *
 * // Track an error
 * addCustomMetric(METRIC_TYPE.ERROR, ERROR_METRIC.API_ERROR, { statusCode: 500, message: error.message }, null, CONTEXT_KEYS.API_SERVICE);
 */
export const addCustomMetric = (metricType: typeof METRIC_TYPE.TRACKING | typeof METRIC_TYPE.ERROR, name: string, context: Record<string, string | number | boolean | undefined | null>, startTime: number | null, contextKey: string, airportCode?: string | undefined) => {
	const metricContext = typeof context === 'object' && context !== null ? context : {};
	const endTime = performance.now();
	const duration = startTime ? (endTime - startTime) / 1000 : null;

	if (metricType === METRIC_TYPE.TRACKING) {
		datadogRum.addAction(name, {
			[contextKey]: {
				...metricContext,
				env: process.env['NODE_ENV'] === 'production' ? 'live' : 'dev',
				channel_id: getAppVariable('channel').id,
				...(startTime && { duration_in_seconds: duration?.toFixed(2) }),
				airport_code: airportCode ? airportCode : getAppVariable('poi').code,
			}
		});
	}

	if (metricType === METRIC_TYPE.ERROR) {
		datadogRum.addError(name, {
			[contextKey]: {
				...metricContext,
				env: process.env['NODE_ENV'] === 'production' ? 'live' : 'dev',
				channel_id: getAppVariable('channel').id,
				...(startTime && { duration_in_seconds: duration?.toFixed(2) }),
				airport_code: airportCode ? airportCode : getAppVariable('poi').code,
			}
		});
	}
}
